import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Camera, MapPin, Users, SprayCan, Palette, Upload, Paintbrush2, Plus, X } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ObjectUploader } from "@/components/ObjectUploader";
import { AdvancedMapControls } from "@/components/AdvancedMapControls";
import { Loader } from "@googlemaps/js-api-loader";

// Simple type declarations for Google Maps
type GoogleMap = any;
type GoogleMarker = any;

interface MapViewProps {
  userLocation: { lat: number; lng: number } | null;
  onPhotoCapture: () => void;
}

export default function MapView({ userLocation, onPhotoCapture }: MapViewProps) {
  const [showLocationPanel, setShowLocationPanel] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [showGraffitiUpload, setShowGraffitiUpload] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<{lat: number; lng: number} | null>(null);
  const [graffitiTitle, setGraffitiTitle] = useState("");
  const [graffitiDescription, setGraffitiDescription] = useState("");
  const [artistName, setArtistName] = useState("");
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMap | null>(null);
  const markersRef = useRef<GoogleMarker[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Add global functions for speech bubble interactions
  useEffect(() => {
    (window as any).likeArt = (discoveryId: string) => {
      toast({
        title: "Liked! 👍",
        description: "You liked this street art piece!",
      });
      console.log("Liked discovery:", discoveryId);
    };

    (window as any).shareArt = (discoveryId: string) => {
      if (navigator.share) {
        navigator.share({
          title: 'Check out this street art!',
          text: 'Amazing street art discovery on LPFTS WORLD WIDE',
          url: window.location.href
        });
      } else {
        navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link Copied! 📤",
          description: "Street art location link copied to clipboard!",
        });
      }
      console.log("Shared discovery:", discoveryId);
    };

    return () => {
      delete (window as any).likeArt;
      delete (window as any).shareArt;
    };
  }, [toast]);

  // Initialize Google Maps
  useEffect(() => {
    if (!mapRef.current || !userLocation || !import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
      console.error("Missing map requirements:", { 
        mapRef: !!mapRef.current, 
        userLocation, 
        apiKey: !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY 
      });
      setMapLoading(false);
      return;
    }

    const initializeMap = async () => {
      try {
        const loader = new Loader({
          apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY!,
          version: "weekly",
          libraries: ["places", "geometry", "visualization", "drawing"]
        });

        await loader.load();
        
        if (!mapRef.current) return;

        // Create custom map types for enhanced street art discovery
        const customMapTypes = {
          // High-contrast satellite for street art detection
          STREET_ART_SATELLITE: new (window as any).google.maps.ImageMapType({
            getTileUrl: function(coord: any, zoom: number) {
              return `https://mt1.google.com/vt/lyrs=s&x=${coord.x}&y=${coord.y}&z=${zoom}`;
            },
            tileSize: new (window as any).google.maps.Size(256, 256),
            name: "Street Art Satellite",
            maxZoom: 22
          }),
          // High-resolution hybrid for detailed analysis
          ENHANCED_HYBRID: new (window as any).google.maps.ImageMapType({
            getTileUrl: function(coord: any, zoom: number) {
              return `https://mt1.google.com/vt/lyrs=y&x=${coord.x}&y=${coord.y}&z=${zoom}`;
            },
            tileSize: new (window as any).google.maps.Size(256, 256),
            name: "Enhanced Hybrid",
            maxZoom: 22
          }),
          // Google Earth-style 3D terrain
          EARTH_3D: new (window as any).google.maps.ImageMapType({
            getTileUrl: function(coord: any, zoom: number) {
              return `https://khms0.googleapis.com/kh?v=943&hl=en&x=${coord.x}&y=${coord.y}&z=${zoom}`;
            },
            tileSize: new (window as any).google.maps.Size(256, 256),
            name: "Earth 3D",
            maxZoom: 22
          })
        };

        const map = new (window as any).google.maps.Map(mapRef.current, {
          center: userLocation,
          zoom: 19, // Higher initial zoom for ultra-detailed graffiti spotting
          maxZoom: 22, // Maximum zoom with custom high-res tiles
          minZoom: 8, // Allow wider area exploration
          mapTypeId: (window as any).google.maps.MapTypeId.SATELLITE,
          
          // Advanced map controls
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: (window as any).google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: (window as any).google.maps.ControlPosition.TOP_LEFT,
            mapTypeIds: [
              'street_art_satellite',
              'enhanced_hybrid', 
              'earth_3d',
              (window as any).google.maps.MapTypeId.SATELLITE,
              (window as any).google.maps.MapTypeId.HYBRID,
              (window as any).google.maps.MapTypeId.ROADMAP,
              (window as any).google.maps.MapTypeId.TERRAIN
            ]
          },
          
          // Enhanced zoom controls
          zoomControl: true,
          zoomControlOptions: {
            position: (window as any).google.maps.ControlPosition.RIGHT_CENTER
          },
          
          // Advanced viewing features
          scaleControl: true,
          scaleControlOptions: {
            position: (window as any).google.maps.ControlPosition.BOTTOM_LEFT
          },
          
          streetViewControl: true,
          streetViewControlOptions: {
            position: (window as any).google.maps.ControlPosition.RIGHT_TOP
          },
          
          rotateControl: true,
          rotateControlOptions: {
            position: (window as any).google.maps.ControlPosition.LEFT_CENTER
          },
          
          // Enhanced tilt and viewing angles for 3D building inspection
          tilt: 45,
          heading: 0,
          
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: (window as any).google.maps.ControlPosition.TOP_RIGHT
          },
          
          gestureHandling: 'greedy', // More responsive for detailed exploration
          backgroundColor: '#0a0a0a',
          
          // Disable default UI elements that interfere with street art hunting
          disableDefaultUI: false,
          clickableIcons: false, // Prevent accidental clicks on POIs
          styles: [
            // Hide all default Points of Interest
            {
              featureType: "poi",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            // Hide transit stations
            {
              featureType: "transit.station",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            // Hide business labels
            {
              featureType: "poi.business",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            // Hide attractions
            {
              featureType: "poi.attraction",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            // Hide parks (but keep basic structure visible)
            {
              featureType: "poi.park",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            },
            // Hide schools, government, medical
            {
              featureType: "poi.school",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.government",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.medical",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            },
            // Keep road labels but make them minimal
            {
              featureType: "road",
              elementType: "labels.text.fill",
              stylers: [{ color: "#ffffff" }]
            },
            {
              featureType: "road",
              elementType: "labels.text.stroke",
              stylers: [{ color: "#000000" }, { weight: 2 }]
            },
            // Keep basic administrative labels
            {
              featureType: "administrative",
              elementType: "labels.text.fill",
              stylers: [{ color: "#ffffff" }]
            },
            {
              featureType: "administrative",
              elementType: "labels.text.stroke",
              stylers: [{ color: "#000000" }, { weight: 2 }]
            }
          ]
        });

        // Register custom map types
        map.mapTypes.set('street_art_satellite', customMapTypes.STREET_ART_SATELLITE);
        map.mapTypes.set('enhanced_hybrid', customMapTypes.ENHANCED_HYBRID);
        map.mapTypes.set('earth_3d', customMapTypes.EARTH_3D);

        // Force 45° imagery at higher zoom levels for better street art viewing
        map.addListener('zoom_changed', () => {
          const zoomLevel = map.getZoom();
          if (zoomLevel >= 18) {
            map.setTilt(45); // Force 45° view for detailed wall inspection
          }
        });

        // Enable 3D buildings for better street art context (removed deprecated heatmap layer)

        // Add custom control for map type switching
        const mapTypeControl = document.createElement('div');
        mapTypeControl.innerHTML = `
          <div style="
            background: rgba(0,0,0,0.8); 
            border-radius: 8px; 
            padding: 8px; 
            margin: 10px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">
            <button id="satellite-btn" style="
              background: #10b981; 
              color: white; 
              border: none; 
              padding: 6px 12px; 
              margin: 2px; 
              border-radius: 4px; 
              cursor: pointer; 
              font-size: 11px;
              font-weight: bold;
            ">🛰️ Ultra HD</button>
            <button id="hybrid-btn" style="
              background: #374151; 
              color: white; 
              border: none; 
              padding: 6px 12px; 
              margin: 2px; 
              border-radius: 4px; 
              cursor: pointer; 
              font-size: 11px;
            ">🏢 Hybrid</button>
            <button id="earth-btn" style="
              background: #374151; 
              color: white; 
              border: none; 
              padding: 6px 12px; 
              margin: 2px; 
              border-radius: 4px; 
              cursor: pointer; 
              font-size: 11px;
            ">🌍 Earth 3D</button>
          </div>
        `;

        // Add event listeners for map type buttons
        mapTypeControl.addEventListener('click', (e: any) => {
          const buttons = mapTypeControl.querySelectorAll('button');
          buttons.forEach(btn => {
            (btn as HTMLElement).style.background = '#374151';
          });

          if (e.target.id === 'satellite-btn') {
            map.setMapTypeId('street_art_satellite');
            e.target.style.background = '#10b981';
          } else if (e.target.id === 'hybrid-btn') {
            map.setMapTypeId('enhanced_hybrid');
            e.target.style.background = '#10b981';
          } else if (e.target.id === 'earth-btn') {
            map.setMapTypeId('earth_3d');
            map.setTilt(45);
            e.target.style.background = '#10b981';
          }
        });

        map.controls[(window as any).google.maps.ControlPosition.TOP_RIGHT].push(mapTypeControl);

        // Enable 3D buildings for street art context
        map.setOptions({
          styles: [
            ...map.get('styles') || [],
            {
              featureType: "all",
              elementType: "labels.text.fill",
              stylers: [{ color: "#ffffff" }]
            },
            {
              featureType: "all", 
              elementType: "labels.text.stroke",
              stylers: [{ color: "#000000" }, { weight: 2 }]
            }
          ]
        });

        mapInstanceRef.current = map;
        setMapLoading(false);

        // Enhanced click handler for precise graffiti location marking
        map.addListener("click", (event: any) => {
          if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            
            // Create temporary marker for click location
            const tempMarker = new (window as any).google.maps.Marker({
              position: { lat, lng },
              map: map,
              icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="15" cy="15" r="12" fill="#ff4444" stroke="#ffffff" stroke-width="2"/>
                    <text x="15" y="20" text-anchor="middle" fill="white" font-size="14">📍</text>
                  </svg>
                `),
                scaledSize: new (window as any).google.maps.Size(30, 30),
                anchor: new (window as any).google.maps.Point(15, 15)
              },
              animation: (window as any).google.maps.Animation.BOUNCE
            });
            
            // Remove temporary marker after 3 seconds
            setTimeout(() => {
              tempMarker.setMap(null);
            }, 3000);
            
            setClickedLocation({ lat, lng });
            setShowGraffitiUpload(true);
          }
        });

        // Add double-click to zoom in for detailed graffiti inspection
        map.addListener("dblclick", (event: any) => {
          if (event.latLng) {
            const currentZoom = map.getZoom();
            const targetZoom = Math.min(currentZoom + 2, 21); // Zoom in 2 levels, max 21
            map.setZoom(targetZoom);
          }
        });

        // Enable high-resolution imagery for graffiti details
        map.setOptions({
          mapTypeControlOptions: {
            ...map.get('mapTypeControlOptions'),
            mapTypeIds: [
              (window as any).google.maps.MapTypeId.SATELLITE,
              (window as any).google.maps.MapTypeId.HYBRID,
              (window as any).google.maps.MapTypeId.ROADMAP
            ]
          }
        });

        // Log successful initialization
        console.log("Google Maps initialized successfully");

      } catch (error) {
        console.error("Error loading Google Maps:", error);
        setMapLoading(false);
        
        // Check for specific API errors
        const errorMessage = error instanceof Error ? error.message : String(error);
        let title = "Map Loading Error";
        let description = "Unable to load Google Maps.";
        
        if (errorMessage.includes("ApiNotActivatedMapError")) {
          title = "Google Maps API Not Activated";
          description = "The Maps JavaScript API needs to be enabled in your Google Cloud Console.";
        } else if (errorMessage.includes("InvalidKeyMapError")) {
          title = "Invalid Google Maps API Key";
          description = "Please check your VITE_GOOGLE_MAPS_API_KEY in environment settings.";
        } else if (errorMessage.includes("RefererNotAllowedMapError")) {
          title = "Domain Not Authorized";
          description = "This domain is not authorized for your Google Maps API key.";
        }
        
        toast({
          title,
          description,
          variant: "destructive",
        });
      }
    };

    initializeMap();
  }, [userLocation]);

  // Fetch nearby discoveries
  const { data: nearbyDiscoveries, isLoading: discoveriesLoading } = useQuery<any[]>({
    queryKey: ["/api/discoveries/nearby", userLocation?.lat, userLocation?.lng],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/discoveries/nearby?latitude=${userLocation?.lat}&longitude=${userLocation?.lng}&radius=4`);
      return response.json();
    },
    enabled: !!userLocation,
  });

  // Temporarily disable user markers to keep clean canvas - will re-enable when players create them
  const nearbyMarkers: any[] = [];

  // Fetch nearby legal graffiti walls
  const { data: nearbyLegalWalls, isLoading: legalWallsLoading } = useQuery<any[]>({
    queryKey: ["/api/legal-walls/nearby", userLocation?.lat, userLocation?.lng],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/legal-walls/nearby?latitude=${userLocation?.lat}&longitude=${userLocation?.lng}&radius=10`);
      return response.json();
    },
    enabled: !!userLocation,
  });

  // Update map markers when data changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Only add legal graffiti walls with brick emoji markers - keeping clean canvas
    if (nearbyLegalWalls) {
      console.log("Adding legal walls to map:", nearbyLegalWalls.length, nearbyLegalWalls);
      nearbyLegalWalls.forEach((wall: any) => {
        // Create enhanced legal wall marker

        // Use regular marker for compatibility
        const brickMarker = new (window as any).google.maps.Marker({
          position: { lat: wall.latitude, lng: wall.longitude },
          map: mapInstanceRef.current,
          title: `🧱 ${wall.name} • Legal Wall`,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#dc2626" stroke="#ffffff" stroke-width="3"/>
                <text x="20" y="26" text-anchor="middle" fill="white" font-size="18">🧱</text>
              </svg>
            `),
            scaledSize: new (window as any).google.maps.Size(40, 40),
            anchor: new (window as any).google.maps.Point(20, 20)
          }
        });

        // Create info window for legal walls
        const infoWindow = new (window as any).google.maps.InfoWindow({
          content: `
            <div class="p-4 max-w-sm">
              <h3 class="font-bold text-lg mb-2">🧱 ${wall.name}</h3>
              <p class="text-sm mb-2">${wall.description}</p>
              <div class="text-xs space-y-1">
                <p><strong>📍 Location:</strong> ${wall.city}, ${wall.country}</p>
                <p><strong>📍 Address:</strong> ${wall.address}</p>
                <p><strong>🕒 Hours:</strong> ${wall.hours}</p>
                <p><strong>📋 Permit:</strong> ${wall.permitRequired ? 'Required' : 'Not Required'}</p>
              </div>
            </div>
          `
        });

        // Add click listener to show info window
        brickMarker.addListener('click', () => {
          infoWindow.open(mapInstanceRef.current, brickMarker);
        });

        markersRef.current.push(brickMarker);
      });
    }
  }, [nearbyLegalWalls]);

  // Mutation to create custom marker
  const createMarkerMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/markers", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Marker Dropped!",
        description: "Your custom marker has been placed.",
      });
    },
    onError: (error) => {
      console.error("Error creating marker:", error);
      toast({
        title: "Error",
        description: "Failed to drop marker. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to upload graffiti discovery
  const uploadGraffitiMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/discoveries/graffiti", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Graffiti Uploaded!",
        description: "Your graffiti discovery has been added to the map. You earned 150 tokens!",
      });
      setShowGraffitiUpload(false);
      setGraffitiTitle("");
      setGraffitiDescription("");
      setArtistName("");
      setClickedLocation(null);
      queryClient.invalidateQueries({ queryKey: ["/api/discoveries/nearby"] });
    },
    onError: (error) => {
      console.error("Error uploading graffiti:", error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload graffiti. Please try again.",
        variant: "destructive",
      });
    },
  });

  const dropCustomMarker = () => {
    if (!userLocation) {
      toast({
        title: "Location Required",
        description: "Location access is required to drop markers.",
        variant: "destructive",
      });
      return;
    }

    createMarkerMutation.mutate({
      name: "Custom Marker",
      description: "User placed marker",
      latitude: userLocation.lat,
      longitude: userLocation.lng,
      markerType: "custom",
    });
  };

  const showArtDetail = (item: any) => {
    setSelectedMarker(item);
    setShowLocationPanel(true);
  };

  return (
    <div className="relative w-full h-full">
      {/* Advanced Mapping Controls Panel */}
      <div className="absolute top-4 left-4 z-30 bg-black/80 backdrop-blur-sm border border-gray-600 rounded-lg p-3">
        <h3 className="text-white font-semibold text-sm mb-2">🛰️ Advanced Imaging</h3>
        <div className="space-y-2">
          <Button
            size="sm"
            variant="ghost"
            className="w-full justify-start text-xs text-gray-300 hover:text-white"
            onClick={() => {
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setMapTypeId('street_art_satellite');
                toast({
                  title: "Ultra HD Satellite",
                  description: "Switched to high-resolution satellite imagery",
                });
              }
            }}
            data-testid="button-ultra-hd-satellite"
          >
            🌍 Ultra HD Satellite
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="w-full justify-start text-xs text-gray-300 hover:text-white"
            onClick={() => {
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setMapTypeId('earth_3d');
                toast({
                  title: "Google Earth 3D",
                  description: "Switched to 3D terrain view",
                });
              }
            }}
            data-testid="button-earth-3d"
          >
            🗻 Earth 3D View
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="w-full justify-start text-xs text-gray-300 hover:text-white"
            onClick={() => {
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setMapTypeId('enhanced_hybrid');
                toast({
                  title: "Enhanced Hybrid",
                  description: "Switched to enhanced hybrid view",
                });
              }
            }}
            data-testid="button-enhanced-hybrid"
          >
            🏢 Enhanced Hybrid
          </Button>
          <div className="border-t border-gray-600 pt-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start text-xs text-gray-300 hover:text-white"
              onClick={() => {
                if (mapInstanceRef.current) {
                  const currentZoom = mapInstanceRef.current.getZoom();
                  const targetZoom = Math.min(currentZoom + 3, 22);
                  mapInstanceRef.current.setZoom(targetZoom);
                  toast({
                    title: "Max Zoom",
                    description: `Zoomed to level ${targetZoom} for detailed inspection`,
                  });
                }
              }}
              data-testid="button-max-zoom"
            >
              🔍 Max Zoom
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start text-xs text-gray-300 hover:text-white"
              onClick={() => setShowAdvancedControls(true)}
              data-testid="button-advanced-controls"
            >
              ⚙️ Advanced Controls
            </Button>
          </div>
        </div>
      </div>

      {/* Map Quality Indicator */}
      <div className="absolute bottom-24 right-4 z-30 bg-black/80 backdrop-blur-sm border border-gray-600 rounded-lg px-3 py-2">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-300">High-Resolution Active</span>
        </div>
      </div>

      {/* Advanced Map Controls */}
      <AdvancedMapControls 
        mapInstance={mapInstanceRef.current}
        isOpen={showAdvancedControls}
        onClose={() => setShowAdvancedControls(false)}
      />

      {/* Google Maps Container */}
      <div 
        ref={mapRef}
        className="w-full h-full bg-dark-base"
        style={{ 
          minHeight: '400px',
          height: '100%',
          width: '100%'
        }}
        data-testid="google-map"
      />
      
      {/* Map Loading Overlay */}
      {mapLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-dark-base to-gray-900 flex items-center justify-center z-20">
          <div className="text-center p-6">
            <div className="animate-spin w-12 h-12 border-4 border-neon-green border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-white text-lg font-semibold">Loading Advanced Mapping</p>
            <p className="text-gray-400 text-sm mt-2">Initializing Google Earth integration & satellite imagery</p>
            <div className="mt-4 flex justify-center space-x-2">
              <div className="w-2 h-2 bg-neon-green rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-coral rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Map Error Fallback */}
      {!mapLoading && !mapInstanceRef.current && (
        <div className="absolute inset-0 bg-dark-base flex items-center justify-center z-20">
          <div className="text-center p-6 max-w-md">
            <div className="w-16 h-16 bg-coral/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="text-coral" size={24} />
            </div>
            <h3 className="text-white text-lg font-semibold mb-2">Google Maps Not Available</h3>
            <p className="text-gray-400 text-sm mb-4">
              The Maps JavaScript API needs to be enabled in your Google Cloud Console.
            </p>
            <div className="text-left bg-dark-elevated p-4 rounded-lg mb-4">
              <p className="text-white text-sm font-medium mb-2">To fix this:</p>
              <ol className="text-gray-300 text-xs space-y-1">
                <li>1. Go to Google Cloud Console</li>
                <li>2. Enable "Maps JavaScript API"</li>
                <li>3. Add this domain to API restrictions</li>
                <li>4. Refresh this page</li>
              </ol>
            </div>
            <Button 
              onClick={() => window.location.reload()} 
              className="mt-2"
              variant="outline"
            >
              Retry Loading Map
            </Button>
          </div>
        </div>
      )}
      


      {/* Bottom Action Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-dark-base/90 backdrop-blur-sm p-4" data-testid="action-bar">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            onClick={onPhotoCapture}
            className="flex flex-col items-center space-y-1 text-neon-green"
            data-testid="button-capture"
          >
            <div className="w-12 h-12 bg-neon-green/20 rounded-full flex items-center justify-center">
              <Camera size={20} />
            </div>
            <span className="text-xs">Capture</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => {
              // Navigate to color chart page
              window.location.href = '/color-chart';
            }}
            className="flex flex-col items-center space-y-1 text-teal-custom hover:text-teal-400 transition-colors"
            data-testid="button-color-chart"
          >
            <div className="w-12 h-12 bg-teal-custom/20 rounded-full flex items-center justify-center hover:bg-teal-custom/30 transition-colors">
              <SprayCan size={20} />
            </div>
            <span className="text-xs">Colors</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => {
              // Navigate to profile page
              window.location.href = '/profile';
            }}
            className="flex flex-col items-center space-y-1 text-coral hover:text-orange-400 transition-colors"
            data-testid="button-profile"
          >
            <div className="w-12 h-12 bg-coral/20 rounded-full flex items-center justify-center hover:bg-coral/30 transition-colors">
              <Users size={20} />
            </div>
            <span className="text-xs">Profile</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => {
              // Navigate to community page
              window.location.href = '/community';
            }}
            className="flex flex-col items-center space-y-1 text-yellow-400 hover:text-yellow-300 transition-colors"
            data-testid="button-community"
          >
            <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center hover:bg-yellow-400/30 transition-colors">
              <Users size={20} />
            </div>
            <span className="text-xs">Community</span>
          </Button>
        </div>
      </div>

      {/* Location Info Panel */}
      {showLocationPanel && selectedMarker && (
        <Card className="absolute bottom-20 left-4 right-4 bg-dark-elevated/95 backdrop-blur-sm border-gray-700 z-15" data-testid="location-panel">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" data-testid="panel-title">{selectedMarker.name}</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowLocationPanel(false)} 
                className="text-gray-400"
                data-testid="button-close-panel"
              >
                ✕
              </Button>
            </div>
            {selectedMarker.description && (
              <p className="text-sm text-gray-300 mb-3" data-testid="panel-description">{selectedMarker.description}</p>
            )}
            {selectedMarker.imageUrl && (
              <img 
                src={selectedMarker.imageUrl} 
                alt={selectedMarker.name}
                className="w-full h-32 object-cover rounded-lg mb-3"
                data-testid="panel-image"
              />
            )}
            <div className="flex space-x-2">
              <span className="bg-neon-green/20 text-neon-green px-2 py-1 rounded text-xs" data-testid="panel-tag-discoveries">
                Street Art
              </span>
              {selectedMarker.type === 'legal_wall' && (
                <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs" data-testid="panel-tag-legal">
                  Legal Wall
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Graffiti Upload Dialog */}
      <Dialog open={showGraffitiUpload} onOpenChange={setShowGraffitiUpload}>
        <DialogContent className="bg-dark-elevated border-gray-700 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Paintbrush2 className="text-coral" size={20} />
                Upload Graffiti Discovery
              </DialogTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowGraffitiUpload(false);
                  setGraffitiTitle("");
                  setGraffitiDescription("");
                  setArtistName("");
                  setClickedLocation(null);
                }}
                className="text-gray-400 hover:text-white"
                data-testid="button-close-graffiti-upload"
              >
                <X size={20} />
              </Button>
            </div>
            <div className="text-sm text-gray-400">
              Fill out the details and upload a photo of your graffiti discovery
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="graffiti-title">Artwork Title</Label>
              <Input
                id="graffiti-title"
                value={graffitiTitle}
                onChange={(e) => setGraffitiTitle(e.target.value)}
                placeholder="Name this piece"
                className="bg-dark-surface border-gray-600 text-white"
                data-testid="input-graffiti-title"
              />
            </div>
            <div>
              <Label htmlFor="artist-name">Artist Name (if known)</Label>
              <Input
                id="artist-name"
                value={artistName}
                onChange={(e) => setArtistName(e.target.value)}
                placeholder="Artist or crew name"
                className="bg-dark-surface border-gray-600 text-white"
                data-testid="input-artist-name"
              />
            </div>
            <div>
              <Label htmlFor="graffiti-description">Description</Label>
              <Textarea
                id="graffiti-description"
                value={graffitiDescription}
                onChange={(e) => setGraffitiDescription(e.target.value)}
                placeholder="Describe the style, colors, technique..."
                className="bg-dark-surface border-gray-600 text-white"
                data-testid="textarea-graffiti-description"
              />
            </div>
            <div>
              <Label>Location</Label>
              <div className="text-sm text-gray-400 bg-dark-surface p-2 rounded border border-gray-600">
                {clickedLocation ? 
                  `${clickedLocation.lat.toFixed(6)}, ${clickedLocation.lng.toFixed(6)}` : 
                  'No location selected'
                }
              </div>
            </div>
            <ObjectUploader
              maxNumberOfFiles={1}
              maxFileSize={10485760} // 10MB
              onGetUploadParameters={async () => {
                const response = await apiRequest("POST", "/api/objects/upload");
                const data = await response.json();
                return {
                  method: "PUT" as const,
                  url: data.uploadURL,
                };
              }}
              onComplete={(result) => {
                if (result.successful && result.successful.length > 0 && graffitiTitle.trim() && clickedLocation) {
                  const uploadUrl = result.successful[0].uploadURL;
                  if (uploadUrl) {
                    uploadGraffitiMutation.mutate({
                      name: graffitiTitle.trim(),
                      description: graffitiDescription.trim(),
                      latitude: clickedLocation.lat,
                      longitude: clickedLocation.lng,
                      imageUrl: uploadUrl,
                      artistName: artistName.trim() || "Unknown",
                      artType: "graffiti",
                    });
                  }
                } else if (result.failed && result.failed.length > 0) {
                  toast({
                    title: "Upload Failed",
                    description: "Failed to upload graffiti photo. Please try again.",
                    variant: "destructive",
                  });
                } else if (!graffitiTitle.trim()) {
                  toast({
                    title: "Title Required",
                    description: "Please enter a title for your graffiti discovery.",
                    variant: "destructive",
                  });
                }
              }}
              buttonClassName="w-full"
              data-testid="button-upload-graffiti"
            >
              <div className="flex items-center justify-center gap-2">
                <Upload size={16} />
                <span>Upload Graffiti Photo</span>
              </div>
            </ObjectUploader>
            <div className="text-xs text-gray-400 bg-dark-surface p-3 rounded border border-gray-600">
              <p className="mb-1"><strong>📍 Tip:</strong> You clicked on the map at this location.</p>
              <p className="mb-1"><strong>🎨 Reward:</strong> Upload graffiti to earn 150 tokens!</p>
              <p><strong>📸 Guidelines:</strong> Only upload photos you took yourself of legal graffiti.</p>
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowGraffitiUpload(false);
                  setGraffitiTitle("");
                  setGraffitiDescription("");
                  setArtistName("");
                  setClickedLocation(null);
                }}
                className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                data-testid="button-cancel-graffiti-upload"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (!graffitiTitle.trim()) {
                    toast({
                      title: "Title Required",
                      description: "Please enter a title for your graffiti discovery before uploading.",
                      variant: "destructive",
                    });
                    return;
                  }
                  // The ObjectUploader will handle the actual upload
                }}
                disabled={!graffitiTitle.trim() || uploadGraffitiMutation.isPending}
                className="flex-1 bg-coral hover:bg-coral/80"
                data-testid="button-ready-to-upload"
              >
                {uploadGraffitiMutation.isPending ? "Uploading..." : "Ready to Upload"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
